/**
 * SessionHeatmap — browser-side session density rendered as a deck.gl
 * HeatmapLayer over a Mapbox basemap.
 *
 * Used by the org analytics page in place of the H3-hexagon SessionMap. The
 * heatmap collapses per-session, per-group detail down to a single density
 * signal — read it as "where are users right now," not "which app are they
 * using." For grouped routing diagnostics, the admin Reports page still uses
 * the hexagon SessionMap.
 *
 * Input is the same `connections` shape SessionMap accepts; only `browserH3`
 * is consumed. Each H3 cell contributes one weighted point at its center
 * (count = sessions in that cell) so co-located sessions intensify the same
 * blob instead of spawning N coincident points.
 *
 * Bundle note: this module also pulls in `mapbox-gl` and `@deck.gl/mapbox`,
 * which are already loaded by SessionMap. If the analytics page ever moves
 * behind a lazy boundary, lazy-load this alongside SessionMap — see the
 * header comment in ./SessionMap.tsx for the playbook.
 */

import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import MapGL, { useControl, type MapRef } from 'react-map-gl/mapbox';
import { MapboxOverlay, type MapboxOverlayProps } from '@deck.gl/mapbox';
import { HeatmapLayer } from 'deck.gl';
import { cellToLatLng, isValidCell } from 'h3-js';
import { logger } from '../../log';
import styles from './SessionMap.module.css';
import 'mapbox-gl/dist/mapbox-gl.css';

const log = logger('session-heatmap');

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as string;

export interface SessionHeatmapProps {
  connections?: Array<{
    sessionId: string;
    browserH3: string | null;
  }>;
  isLoading?: boolean;
}

type HeatmapPoint = { position: [number, number]; weight: number };

// Aggregate sessions by H3 cell so multiple sessions in the same cell add
// intensity at a single point instead of spawning N coincident points. The
// HeatmapLayer's GPU pass would otherwise re-aggregate them anyway, but
// pre-aggregating shrinks the data array we hand to the GPU on every render.
function buildHeatmapPoints(connections: SessionHeatmapProps['connections']): HeatmapPoint[] {
  if (!connections || connections.length === 0) return [];
  const counts = new Map<string, number>();
  for (const conn of connections) {
    const h3 = conn.browserH3;
    if (!h3) continue;
    counts.set(h3, (counts.get(h3) ?? 0) + 1);
  }
  const points: HeatmapPoint[] = [];
  for (const [h3, count] of counts) {
    if (!isValidCell(h3)) continue;
    try {
      const [lat, lng] = cellToLatLng(h3);
      points.push({ position: [lng, lat], weight: count });
    } catch {
      // skip malformed cells silently — they just don't contribute heat
    }
  }
  return points;
}

// Bridge between deck.gl and react-map-gl — same shape SessionMap uses.
function DeckGLOverlay(props: MapboxOverlayProps) {
  const overlay = useControl<MapboxOverlay>(() => new MapboxOverlay(props));
  overlay.setProps(props);
  return null;
}

// Cyan→amber→magenta ramp, low alpha at the bottom so sparse cells fade into
// the basemap and the dense centers pop. Kept in a module constant so React
// doesn't think the prop changed on every render.
const HEATMAP_COLOR_RANGE: [number, number, number, number][] = [
  [0, 212, 255, 25],
  [0, 212, 255, 90],
  [120, 200, 255, 160],
  [255, 220, 120, 200],
  [245, 158, 11, 220],
  [232, 65, 70, 240],
];

export function SessionHeatmap({ connections = [], isLoading }: SessionHeatmapProps) {
  const mapRef = useRef<MapRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');
  const [mapError, setMapError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    if (!containerRef.current) return;
    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      log.error('fullscreen error', err);
    }
  }, []);

  useEffect(() => {
    const update = () => {
      const dataTheme = document.documentElement.getAttribute('data-theme');
      setTheme(dataTheme?.includes('light') ? 'light' : 'dark');
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => observer.disconnect();
  }, []);

  const points = useMemo(() => {
    try {
      return buildHeatmapPoints(connections);
    } catch (e) {
      log.error('error building heatmap points', e);
      return [];
    }
  }, [connections]);

  const deckLayers = useMemo(() => {
    if (points.length === 0) return [];
    return [
      new HeatmapLayer<HeatmapPoint>({
        id: 'session-heatmap',
        data: points,
        getPosition: (d) => d.position,
        getWeight: (d) => d.weight,
        // 60px gives readable continent-scale blobs without smearing dense
        // metros into a single blur. Tune alongside `intensity` if a future
        // dataset is much sparser or denser.
        radiusPixels: 60,
        intensity: 1,
        threshold: 0.05,
        aggregation: 'SUM',
        colorRange: HEATMAP_COLOR_RANGE,
      }),
    ];
  }, [points]);

  if (!MAPBOX_TOKEN) {
    return (
      <div className={styles.mapContainer}>
        <div className={styles.fallback}>
          <div className={styles.fallbackIcon}>🗺️</div>
          <div className={styles.fallbackText}>Map unavailable</div>
          <div className={styles.fallbackHint}>VITE_MAPBOX_ACCESS_TOKEN not configured</div>
        </div>
      </div>
    );
  }

  if (mapError) {
    return (
      <div className={styles.mapContainer}>
        <div className={styles.fallback}>
          <div className={styles.fallbackIcon}>⚠️</div>
          <div className={styles.fallbackText}>Map error</div>
          <div className={styles.fallbackHint}>{mapError}</div>
        </div>
      </div>
    );
  }

  const mapStyle = theme === 'light'
    ? 'mapbox://styles/mapbox/light-v11'
    : 'mapbox://styles/mapbox/dark-v11';

  return (
    <div
      ref={containerRef}
      className={`${styles.mapContainer} ${isLoading ? styles.loading : ''} ${isFullscreen ? styles.fullscreen : ''}`}
    >
      <MapGL
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{
          longitude: 0,
          latitude: 25,
          zoom: 1.3,
          pitch: 0,
        }}
        projection="mercator"
        style={{ width: '100%', height: '100%' }}
        mapStyle={mapStyle}
        styleDiffing={false}
        collectResourceTiming={false}
        trackResize={true}
        attributionControl={false}
        onError={(e) => {
          const message = e.error?.message || 'Unknown map error';
          log.error('map error', e);
          setMapError(message);
        }}
      >
        <DeckGLOverlay layers={deckLayers} />
      </MapGL>

      <button
        onClick={toggleFullscreen}
        className={styles.fullscreenButton}
        aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
      >
        {isFullscreen ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" />
          </svg>
        )}
      </button>
    </div>
  );
}

export default SessionHeatmap;
